import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Activity, LayoutDashboard, Mic, FileHeart, Users, FlaskConical, ShieldCheck,
  Settings2, ScrollText, LogOut, Dna, ClipboardList,
} from "lucide-react";

const NAV = {
  patient: [
    { to: "/patient", label: "My Dashboard", icon: LayoutDashboard, end: true },
    { to: "/patient/record", label: "Record Voice", icon: Mic },
    { to: "/patient/consent", label: "Consent", icon: ShieldCheck },
  ],
  clinician: [
    { to: "/clinician", label: "Patients", icon: Users, end: true },
    { to: "/clinician/mapping", label: "Test Mapping", icon: Dna },
  ],
  lab: [{ to: "/lab", label: "Test Queue", icon: FlaskConical, end: true }],
  admin: [
    { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
    { to: "/admin/users", label: "Users", icon: Users },
    { to: "/admin/mapping", label: "Test Mapping", icon: Dna },
    { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
  ],
};

const ROLE_LABEL = { patient: "Patient", clinician: "Clinician", lab: "Lab Partner", admin: "Administrator" };

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const items = NAV[user.role] || [];

  const doLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-slate-200 bg-white sticky top-0 h-screen">
        <div className="px-6 py-6 border-b border-slate-100">
          <img src="/manogene-logo.png" alt="ManoGene" className="h-7 w-auto" />
          <div className="eyebrow mt-2">{ROLE_LABEL[user.role]}</div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                  isActive ? "bg-brand text-white" : "text-ink-soft hover:bg-slate-100"
                }`
              }
            >
              <it.icon className="h-4.5 w-4.5 h-[18px] w-[18px]" />
              {it.label}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-4 border-t border-slate-100">
          {user.manogene_id && (
            <div className="px-3 pb-3">
              <div className="eyebrow">MANOGENE ID</div>
              <div className="mono text-sm font-semibold text-brand mt-0.5">{user.manogene_id}</div>
            </div>
          )}
          <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50">
            <div className="h-8 w-8 rounded-full bg-brand-accent/15 flex items-center justify-center text-brand font-semibold text-sm">
              {user.name?.[0]?.toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-ink truncate">{user.name}</div>
              <div className="text-xs text-ink-muted truncate">{user.email}</div>
            </div>
          </div>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="mt-2 w-full flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-ink-soft hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden sticky top-0 z-20 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <img src="/manogene-logo.png" alt="ManoGene" className="h-6 w-auto" />
          <button onClick={doLogout} className="text-ink-soft" data-testid="logout-btn-mobile">
            <LogOut className="h-5 w-5" />
          </button>
        </header>

        {/* Mobile bottom nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-20 bg-white border-t border-slate-200 flex justify-around py-1.5">
          {items.slice(0, 4).map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] font-medium ${
                  isActive ? "text-brand" : "text-ink-muted"
                }`
              }
            >
              <it.icon className="h-5 w-5" />
              {it.label.split(" ")[0]}
            </NavLink>
          ))}
        </nav>

        <main className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-10 pb-24 md:pb-10 max-w-[1400px] w-full mx-auto">
          {children}
        </main>
      </div>
    </div>
  );
}

export { ClipboardList, FileHeart, Settings2 };
